// Demo Data Service - Realistic factory simulation for 5 CNC machines
// Generates time-varying data that feels alive without Firebase

const MACHINES = [
  {
    id: 'f_fanuc_01',
    name: 'FANUC Robodrill',
    manufacturer: 'FANUC',
    model: '\u03B1-D21MiB5',
    type: 'cnc_mill',
    monitorType: 'focas',
    location: 'Cell A \u2013 Station 1',
    ip: '192.168.1.101',
    axes: ['X', 'Y', 'Z', 'A', 'C'],
    spindleMax: 24000,
    toolCapacity: 21,
    powerRating: 11,
  },
  {
    id: 'bsr_haas_01',
    name: 'Haas VF-2SS',
    manufacturer: 'Haas',
    model: 'VF-2SS',
    type: 'cnc_mill',
    monitorType: 'bsr_monitor',
    location: 'Cell B \u2013 Station 1',
    ip: '192.168.1.102',
    axes: ['X', 'Y', 'Z'],
    spindleMax: 12000,
    toolCapacity: 24,
    powerRating: 22.4,
  },
  {
    id: 'bsr_mazak_01',
    name: 'Mazak Quick Turn 250',
    manufacturer: 'Mazak',
    model: 'Quick Turn 250MY',
    type: 'cnc_lathe',
    monitorType: 'bsr_monitor',
    location: 'Cell B \u2013 Station 2',
    ip: '192.168.1.103',
    axes: ['X', 'Z', 'C', 'Y'],
    spindleMax: 5000,
    toolCapacity: 12,
    powerRating: 18.5,
  },
  {
    id: 'bsr_dmg_01',
    name: 'DMG MORI NLX 2500',
    manufacturer: 'DMG MORI',
    model: 'NLX 2500/700',
    type: 'cnc_lathe',
    monitorType: 'bsr_monitor',
    location: 'Cell C \u2013 Station 1',
    ip: '192.168.1.104',
    axes: ['X', 'Z', 'C'],
    spindleMax: 4000,
    toolCapacity: 12,
    powerRating: 15,
  },
  {
    id: 'trumpf_01',
    name: 'TRUMPF TruLaser 3030',
    manufacturer: 'TRUMPF',
    model: 'TruLaser 3030 fiber',
    type: 'laser_cutter',
    monitorType: 'digital_io',
    location: 'Cell D \u2013 Station 1',
    ip: '192.168.1.105',
    channels: 8,
    powerRating: 30,
  },
];

const PROGRAMS = {
  f_fanuc_01: { number: 'O1234', name: 'BRACKET-A7', cycleTime: 45, target: 200 },
  bsr_haas_01: { number: 'O5678', name: 'HOUSING-B3', cycleTime: 90, target: 150 },
  bsr_mazak_01: { number: 'O9012', name: 'SHAFT-C12', cycleTime: 60, target: 300 },
  bsr_dmg_01: { number: 'O3456', name: 'BUSHING-D4', cycleTime: 35, target: 50 },
  trumpf_01: { number: 'P1001', name: 'PANEL-E8', cycleTime: 120, target: 60 },
};

const ALARMS = [
  {
    machineId: 'bsr_mazak_01',
    code: 'VIB-001',
    severity: 'warning',
    message: 'Spindle vibration exceeding normal threshold',
    description: 'RMS velocity at 2.1 mm/s (threshold: 1.8 mm/s). Schedule maintenance within 48 hours.',
    timestamp: Date.now() - 1200000,
    acknowledged: false,
  },
];

// Smooth noise function for realistic value variation
function vary(base, amplitude, periodSeconds, offset = 0) {
  const t = Date.now() / 1000;
  return base + amplitude * Math.sin((t + offset) * (2 * Math.PI / periodSeconds));
}

// Add small random jitter
function jitter(value, percent = 2) {
  return value * (1 + (Math.random() - 0.5) * 2 * (percent / 100));
}

// Clamp value
function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

class DemoDataService {
  constructor() {
    this.subscriptions = new Map();
    this.intervalIds = new Map();
    this.startTime = Date.now();
    this.partCounters = {};

    // Initialize part counters at controlled percentages for demo appeal
    const initialPercent = {
      f_fanuc_01: 0.58,      // ~116/200 — actively running
      bsr_haas_01: 0.53,     // ~80/150 — actively running
      bsr_mazak_01: 0.63,    // ~189/300 — running but has vibration issue
      bsr_dmg_01: 1.0,       // 50/50 — batch complete, idle
      trumpf_01: 0.65,       // ~39/60 — actively cutting
    };
    MACHINES.forEach(m => {
      const prog = PROGRAMS[m.id];
      if (prog) {
        const pct = initialPercent[m.id] || 0.6;
        this.partCounters[m.id] = Math.floor(prog.target * pct);
      }
    });
  }

  // --- Data generation per machine ---

  _generateFanucData(machine) {
    const prog = PROGRAMS[machine.id];
    const elapsed = (Date.now() - this.startTime) / 1000;
    const cyclePos = (elapsed % prog.cycleTime) / prog.cycleTime;

    // Increment parts
    if (elapsed > 0 && Math.floor(elapsed / prog.cycleTime) > Math.floor((elapsed - 3) / prog.cycleTime)) {
      this.partCounters[machine.id] = Math.min(this.partCounters[machine.id] + 1, prog.target);
    }

    // Power follows cutting profile
    let power;
    if (cyclePos < 0.08) power = vary(3200, 400, 8, 1); // rapid approach
    else if (cyclePos < 0.85) power = vary(8500, 1200, 12, 2); // cutting
    else power = vary(2800, 300, 6, 3); // rapid retract

    const spindleSpeed = cyclePos < 0.08 || cyclePos > 0.85 ? 0 : vary(12000, 500, 20, 4);
    const spindleLoad = spindleSpeed > 0 ? vary(45, 12, 15, 5) : 0;

    return {
      id: machine.id,
      ...machine,
      currentState: {
        execution: 'ACTIVE',
        mode: 'AUTOMATIC',
        emergency: false,
        alarm: false,
      },
      state: { execution: 'ACTIVE', mode: 'AUTOMATIC' },
      currentProduction: {
        programNumber: prog.number,
        programName: prog.name,
        piecesProduced: this.partCounters[machine.id],
        piecesTarget: prog.target,
        currentCycleTime: Math.floor(cyclePos * prog.cycleTime),
        averageCycleTime: prog.cycleTime,
      },
      production: {
        programNumber: prog.number,
        programName: prog.name,
        piecesProduced: this.partCounters[machine.id],
        piecesTarget: prog.target,
        currentCycleTime: Math.floor(cyclePos * prog.cycleTime),
        averageCycleTime: prog.cycleTime,
      },
      energyReading: {
        powerWatts: jitter(clamp(power, 1500, 11000)),
        voltageVolts: vary(220, 3, 30, 6),
        currentAmps: jitter(clamp(power, 1500, 11000)) / 220 / 0.92,
        powerFactor: vary(0.92, 0.03, 25, 7),
        totalEnergyWh: 42500 + elapsed * 2.4,
      },
      energy: {
        powerWatts: jitter(clamp(power, 1500, 11000)),
        voltageVolts: vary(220, 3, 30, 6),
        currentAmps: jitter(clamp(power, 1500, 11000)) / 220 / 0.92,
        powerFactor: vary(0.92, 0.03, 25, 7),
        totalEnergyWh: 42500 + elapsed * 2.4,
      },
      axis: {
        X: { position: vary(125.4, 80, prog.cycleTime * 0.7, 10).toFixed(3), load: clamp(vary(23, 10, 8, 11), 5, 65), temperature: vary(42, 2, 120, 12).toFixed(1) },
        Y: { position: vary(-78.2, 60, prog.cycleTime * 0.9, 13).toFixed(3), load: clamp(vary(31, 8, 10, 14), 5, 55), temperature: vary(44, 2, 130, 15).toFixed(1) },
        Z: { position: vary(-45.7, 40, prog.cycleTime * 0.5, 16).toFixed(3), load: clamp(vary(18, 6, 7, 17), 3, 45), temperature: vary(40, 1.5, 140, 18).toFixed(1) },
        A: { position: vary(0, 15, prog.cycleTime * 1.2, 19).toFixed(3), load: clamp(vary(12, 5, 9, 20), 2, 35), temperature: vary(38, 1, 150, 21).toFixed(1) },
        C: { position: vary(0, 180, prog.cycleTime * 1.5, 22).toFixed(3), load: clamp(vary(8, 4, 11, 23), 1, 25), temperature: vary(37, 1, 160, 24).toFixed(1) },
      },
      spindle: {
        speed: Math.round(clamp(spindleSpeed, 0, 24000)),
        load: Math.round(clamp(spindleLoad, 0, 100)),
        direction: 'CW',
        temperature: vary(52, 3, 90, 25).toFixed(1),
      },
      activeAlarms: [],
      alarms: [],
      lastUpdate: Date.now(),
      timestamp: Date.now(),
    };
  }

  _generateBSRData(machine, vibrationProfile = 'normal') {
    const prog = PROGRAMS[machine.id];
    const elapsed = (Date.now() - this.startTime) / 1000;
    const elapsedHours = elapsed / 3600;
    const isIdle = machine.id === 'bsr_dmg_01';
    const cyclePos = isIdle ? 0 : (elapsed % prog.cycleTime) / prog.cycleTime;

    // Increment parts
    if (!isIdle && elapsed > 0 && Math.floor(elapsed / prog.cycleTime) > Math.floor((elapsed - 3) / prog.cycleTime)) {
      this.partCounters[machine.id] = Math.min(this.partCounters[machine.id] + 1, prog.target);
    }

    // Power profile
    let power;
    if (isIdle) {
      power = vary(2300, 200, 15, 30);
    } else if (cyclePos < 0.1) {
      power = vary(machine.powerRating * 300, 500, 10, 31);
    } else if (cyclePos < 0.85) {
      power = vary(machine.powerRating * 700, machine.powerRating * 100, 14, 32);
    } else {
      power = vary(machine.powerRating * 250, 300, 8, 33);
    }

    // Vibration - Mazak has rising vibration (predictive maintenance scenario)
    // Starts ABOVE threshold so warning shows immediately in demos
    let vibration;
    if (vibrationProfile === 'rising') {
      const baseVib = 1.95 + elapsedHours * 0.03; // starts at 1.95, slowly rises
      vibration = {
        rmsVelocity: parseFloat(jitter(vary(baseVib, 0.12, 20, 40), 3).toFixed(2)),
        peakAcceleration: parseFloat(jitter(vary(baseVib * 0.55, 0.08, 18, 41), 3).toFixed(2)),
        temperature: parseFloat(vary(56 + elapsedHours * 0.3, 2, 60, 42).toFixed(1)),
        status: 'warning', // Always warning — above threshold from start
        trend: 'rising',
        threshold: 1.8,
      };
    } else if (isIdle) {
      vibration = {
        rmsVelocity: parseFloat(vary(0.15, 0.05, 30, 43).toFixed(2)),
        peakAcceleration: parseFloat(vary(0.08, 0.02, 25, 44).toFixed(2)),
        temperature: parseFloat(vary(32, 1, 120, 45).toFixed(1)),
        status: 'normal',
        trend: 'stable',
        threshold: 1.8,
      };
    } else {
      vibration = {
        rmsVelocity: parseFloat(jitter(vary(0.7, 0.12, 22, 46), 2).toFixed(2)),
        peakAcceleration: parseFloat(jitter(vary(0.4, 0.08, 19, 47), 2).toFixed(2)),
        temperature: parseFloat(vary(48, 2, 80, 48).toFixed(1)),
        status: 'normal',
        trend: 'stable',
        threshold: 1.8,
      };
    }

    const execution = isIdle ? 'READY' : 'ACTIVE';
    const mode = isIdle ? 'MANUAL' : 'AUTOMATIC';

    // Mazak always gets the vibration alarm (above threshold from start)
    const alarms = vibrationProfile === 'rising'
      ? [{
          ...ALARMS[0],
          description: `RMS velocity at ${vibration.rmsVelocity} mm/s (threshold: 1.8 mm/s). Schedule maintenance within 48 hours.`,
          timestamp: Date.now() - 1200000,
        }]
      : [];

    // BSR Monitor: energy + vibration ONLY. No production, no spindle, no axes.
    const powerVal = jitter(clamp(power, 800, machine.powerRating * 1000));
    return {
      id: machine.id,
      ...machine,
      currentState: {
        execution,
        mode,
        emergency: false,
        alarm: alarms.length > 0,
      },
      state: { execution, mode },
      // Energy from Shelly EM
      energyReading: {
        powerWatts: powerVal,
        voltageVolts: vary(220, 4, 35, 51),
        currentAmps: powerVal / 220 / 0.89,
        powerFactor: vary(0.89, 0.04, 28, 52),
        totalEnergyWh: 38000 + elapsed * (isIdle ? 0.6 : 3.2),
      },
      energy: {
        powerWatts: powerVal,
        voltageVolts: vary(220, 4, 35, 51),
        currentAmps: powerVal / 220 / 0.89,
        powerFactor: vary(0.89, 0.04, 28, 52),
        totalEnergyWh: 38000 + elapsed * (isIdle ? 0.6 : 3.2),
      },
      // Vibration from BSR accelerometer
      vibration,
      activeAlarms: alarms,
      alarms,
      lastUpdate: Date.now(),
      timestamp: Date.now(),
    };
  }

  _generateTrumpfData(machine) {
    const elapsed = (Date.now() - this.startTime) / 1000;
    const cyclePeriod = 120; // approximate cycle for channel toggling
    const cyclePos = (elapsed % cyclePeriod) / cyclePeriod;

    // Digital I/O: 8 boolean channels — this is ALL the hardware provides
    const channels = {
      1: true,
      2: true,
      3: cyclePos > 0.05 && cyclePos < 0.9,
      4: cyclePos < 0.05 || cyclePos > 0.9,
      5: cyclePos > 0.03 && cyclePos < 0.92,
      6: true,
      7: true,
      8: true,
    };

    // Derive execution state from channel states
    const anyActive = Object.values(channels).some(v => v);
    const execution = anyActive ? 'ACTIVE' : 'READY';

    return {
      id: machine.id,
      ...machine,
      currentState: {
        execution,
        mode: 'AUTOMATIC',
        emergency: false,
        alarm: false,
        channels,
      },
      state: { execution, mode: 'AUTOMATIC' },
      // Channel labels from user configuration
      channelLabels: {
        1: 'Machine ON', 2: 'Program Running', 3: 'Laser Active',
        4: 'Material Feed', 5: 'Gas Flow', 6: 'Exhaust Fan',
        7: 'Safety Door', 8: 'Cooling System',
      },
      digitalInputs: { channels },
      // NO energyReading — the Pi HAT doesn't measure power
      // NO currentProduction — no CNC interface
      activeAlarms: [],
      alarms: [],
      lastUpdate: Date.now(),
      timestamp: Date.now(),
    };
  }

  generateMachineData(machineId) {
    const machine = MACHINES.find(m => m.id === machineId);
    if (!machine) return null;

    switch (machine.monitorType) {
      case 'focas':
        return this._generateFanucData(machine);
      case 'bsr_monitor':
        if (machineId === 'bsr_mazak_01') return this._generateBSRData(machine, 'rising');
        return this._generateBSRData(machine);
      case 'digital_io':
        return this._generateTrumpfData(machine);
      default:
        return null;
    }
  }

  // --- API methods matching RealtimeDataService ---

  subscribeMachineState(machineId, callback) {
    // Send initial data asynchronously (matches Firebase onSnapshot behavior)
    queueMicrotask(() => {
      const data = this.generateMachineData(machineId);
      if (data) callback(data, null);
    });

    // Update every 2.5 seconds
    const intervalId = setInterval(() => {
      const freshData = this.generateMachineData(machineId);
      if (freshData) callback(freshData, null);
    }, 2500);

    this.intervalIds.set(`machine_${machineId}`, intervalId);
    return () => {
      clearInterval(intervalId);
      this.intervalIds.delete(`machine_${machineId}`);
    };
  }

  subscribeAllMachines(callback) {
    // Deliver asynchronously to match Firebase onSnapshot behavior
    queueMicrotask(() => {
      callback(MACHINES.map(m => ({ ...m })), null);
    });
    return () => {};
  }

  subscribeEnergyData(deviceId, callback) {
    const sendData = () => {
      const fullData = this.generateMachineData(deviceId);
      if (fullData) {
        callback({
          deviceId,
          powerWatts: fullData.energy.powerWatts,
          voltageVolts: fullData.energy.voltageVolts,
          currentAmps: fullData.energy.currentAmps,
          powerFactor: fullData.energy.powerFactor,
          totalEnergyWh: fullData.energy.totalEnergyWh,
          totalReturnedWh: 0,
          timestamp: Date.now(),
        }, null);
      }
    };

    queueMicrotask(sendData);
    const intervalId = setInterval(sendData, 3000);
    this.intervalIds.set(`energy_${deviceId}`, intervalId);
    return () => {
      clearInterval(intervalId);
      this.intervalIds.delete(`energy_${deviceId}`);
    };
  }

  subscribeMachineAlarms(machineId, callback) {
    const sendAlarms = () => {
      const fullData = this.generateMachineData(machineId);
      callback(fullData?.alarms || [], null);
    };

    queueMicrotask(sendAlarms);
    const intervalId = setInterval(sendAlarms, 5000);
    this.intervalIds.set(`alarms_${machineId}`, intervalId);
    return () => {
      clearInterval(intervalId);
      this.intervalIds.delete(`alarms_${machineId}`);
    };
  }

  subscribeProductionData(machineId, callback) {
    const sendData = () => {
      const fullData = this.generateMachineData(machineId);
      if (fullData) callback(fullData.production, null);
    };

    queueMicrotask(sendData);
    const intervalId = setInterval(sendData, 3000);
    this.intervalIds.set(`production_${machineId}`, intervalId);
    return () => {
      clearInterval(intervalId);
      this.intervalIds.delete(`production_${machineId}`);
    };
  }

  // Generate historical data for charts
  async getHistoricalData(machineId, hours = 24) {
    const history = [];
    const now = Date.now();
    const machine = MACHINES.find(m => m.id === machineId);
    const prog = PROGRAMS[machineId];
    if (!machine || !prog) return history;

    const pointsPerHour = 4;
    const totalPoints = hours * pointsPerHour;
    const isIdle = machineId === 'bsr_dmg_01';

    for (let i = totalPoints; i >= 0; i--) {
      const timestamp = now - i * (3600000 / pointsPerHour);
      const hourOfDay = new Date(timestamp).getHours();
      const isWorkHours = hourOfDay >= 6 && hourOfDay <= 22;

      // Simulate shift patterns
      let powerBase = isWorkHours
        ? (isIdle ? machine.powerRating * 150 : machine.powerRating * 650)
        : machine.powerRating * 80;

      const power = powerBase * (0.85 + Math.sin(i * 0.3) * 0.15);
      const partsAtTime = isIdle
        ? prog.target
        : Math.floor((prog.target * (totalPoints - i)) / totalPoints * (isWorkHours ? 1 : 0.1));

      history.push({
        id: `hist_${machineId}_${i}`,
        machineId,
        timestamp,
        state: { execution: isWorkHours ? (isIdle ? 'READY' : 'ACTIVE') : 'STOPPED', mode: 'AUTOMATIC' },
        production: {
          programNumber: prog.number,
          programName: prog.name,
          piecesProduced: Math.min(partsAtTime, prog.target),
          piecesTarget: prog.target,
          averageCycleTime: prog.cycleTime,
        },
        energy: {
          powerWatts: clamp(power, 500, machine.powerRating * 1000),
        },
        connection: { online: true, healthy: true },
      });
    }

    return history;
  }

  async getEnergyHistory(deviceId, hours = 24) {
    const history = [];
    const now = Date.now();
    const machineIds = deviceId === 'all' ? MACHINES.map(m => m.id) : [deviceId];

    for (const mid of machineIds) {
      const machine = MACHINES.find(m => m.id === mid);
      if (!machine) continue;

      const pointsPerHour = 4;
      const totalPoints = hours * pointsPerHour;

      for (let i = totalPoints; i >= 0; i--) {
        const timestamp = now - i * (3600000 / pointsPerHour);
        const hourOfDay = new Date(timestamp).getHours();
        const isWorkHours = hourOfDay >= 6 && hourOfDay <= 22;

        let powerBase = isWorkHours ? machine.powerRating * 650 : machine.powerRating * 80;
        if (mid === 'bsr_dmg_01') powerBase = isWorkHours ? machine.powerRating * 150 : machine.powerRating * 60;

        const power = powerBase * (0.85 + Math.sin(i * 0.3 + MACHINES.indexOf(machine)) * 0.15);

        history.push({
          id: `energy_${mid}_${i}`,
          deviceId: mid,
          powerWatts: clamp(power, 300, machine.powerRating * 1000),
          voltageVolts: 220 + Math.sin(i * 0.1) * 3,
          currentAmps: clamp(power, 300, machine.powerRating * 1000) / 220 / 0.9,
          powerFactor: 0.9 + Math.sin(i * 0.2) * 0.04,
          totalEnergyWh: 30000 + (totalPoints - i) * power / (pointsPerHour),
          totalReturnedWh: 0,
          timestamp,
        });
      }
    }

    return history.sort((a, b) => a.timestamp - b.timestamp);
  }

  async getProductionStats(machineId, days = 7) {
    const prog = PROGRAMS[machineId];
    if (!prog) return { totalParts: 0, totalRuntime: 0, totalDowntime: 0, availability: '0', oee: '0', period: `${days} days` };

    const partsPerDay = (16 * 3600) / prog.cycleTime * 0.78; // 16h work, 78% efficiency
    return {
      totalParts: Math.floor(partsPerDay * days),
      totalRuntime: Math.floor(days * 16 * 60),
      totalDowntime: Math.floor(days * 8 * 60),
      availability: '85.2',
      oee: '72.4',
      period: `${days} days`,
    };
  }

  async getAllActiveAlarms() {
    const allAlarms = [];
    MACHINES.forEach(machine => {
      const data = this.generateMachineData(machine.id);
      if (data?.alarms?.length) {
        data.alarms.forEach(alarm => {
          allAlarms.push({
            ...alarm,
            machineName: machine.name,
          });
        });
      }
    });
    return allAlarms;
  }

  // Get vibration history for maintenance dashboard
  async getVibrationHistory(machineId, hours = 24) {
    const machine = MACHINES.find(m => m.id === machineId);
    if (!machine || machine.monitorType !== 'bsr_monitor') return [];

    const history = [];
    const now = Date.now();
    const pointsPerHour = 6;
    const totalPoints = hours * pointsPerHour;
    const isRising = machineId === 'bsr_mazak_01';

    for (let i = totalPoints; i >= 0; i--) {
      const timestamp = now - i * (3600000 / pointsPerHour);
      const hourOfDay = new Date(timestamp).getHours();
      const isWorkHours = hourOfDay >= 6 && hourOfDay <= 22;
      const hoursAgo = i / pointsPerHour;

      let rmsBase;
      if (isRising) {
        rmsBase = isWorkHours ? 1.5 + (hours - hoursAgo) * 0.025 : 0.15;
      } else if (machineId === 'bsr_dmg_01') {
        rmsBase = isWorkHours ? 0.5 : 0.1;
      } else {
        rmsBase = isWorkHours ? 0.7 : 0.12;
      }

      history.push({
        timestamp,
        rmsVelocity: parseFloat((rmsBase + Math.sin(i * 0.4) * 0.08).toFixed(2)),
        peakAcceleration: parseFloat((rmsBase * 0.55 + Math.sin(i * 0.3) * 0.05).toFixed(2)),
        temperature: parseFloat((isWorkHours ? 48 + Math.sin(i * 0.2) * 3 : 30 + Math.sin(i * 0.1) * 2).toFixed(1)),
      });
    }

    return history;
  }

  subscribeRealtimeMachineData(machineId, callback) {
    return this.subscribeMachineState(machineId, callback);
  }

  batchSubscribe(machineIds, dataTypes, callback) {
    const subscriptions = [];
    machineIds.forEach(machineId => {
      if (dataTypes.includes('state')) {
        subscriptions.push(this.subscribeMachineState(machineId, (data, error) => callback(machineId, 'state', data, error)));
      }
      if (dataTypes.includes('energy')) {
        subscriptions.push(this.subscribeEnergyData(machineId, (data, error) => callback(machineId, 'energy', data, error)));
      }
    });
    return subscriptions;
  }

  unsubscribeAll() {
    this.intervalIds.forEach(id => clearInterval(id));
    this.intervalIds.clear();
  }

  unsubscribe(key) {
    const id = this.intervalIds.get(key);
    if (id) {
      clearInterval(id);
      this.intervalIds.delete(key);
    }
  }
}

const demoDataService = new DemoDataService();
export default demoDataService;
export { DemoDataService, MACHINES, PROGRAMS };
